import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Textarea from 'react-textarea-autosize';
import Section from '../Section';
import { ContactFormContext } from '@views/Contact/Form';

type FieldType = 'text' | 'select' | 'repeater' | 'toggle' | 'year' | 'date' | 'birthdate';

interface FormField {
  type: FieldType;
  label: string;
  key: string;
  value?: any;
  size?: number;
  prefix?: string;
  form?: Array<FormField>; // if field is a repeater
  options?: { [key: string]: string };
  order?: Array<string>;
  placeholder?: string;
}

interface FormSection {
  label: string;
  type: 'section';
  fields: Array<FormField>;
}

const yearRange = buildYears();
const months = buildMonths();
const dayRange = [...Array(31).keys()].map((x) => x + 1);

function determineType(type) {
  switch (type) {
    case 'all_locations':
      return 'Location';
    case 'all_organizations':
      return 'Organization';
    case 'all_websites':
      return 'Website';
    case 'all_education':
      return 'Education';
  }

  return '';
}

function buildYears() {
  let year = 1900;
  const end = new Date().getFullYear() + 1;
  const years = [];

  while (year <= end) {
    years.push(year);
    year += 1;
  }

  years.reverse();
  return years;
}

function buildMonths() {
  const date = new Date();
  date.setDate(1);
  date.setMonth(0);
  const months = {};

  while (true) {
    months[date.getMonth() + 1] = date.toLocaleDateString('default', { month: 'long' });

    if (date.getMonth() >= 11) {
      break;
    }

    date.setMonth(date.getMonth() + 1);
  }

  return months;
}

type GroupProps = React.ComponentPropsWithoutRef<'div'> & { item: FormField };

export function Group({ className, item, ...props }: GroupProps) {
  const { size } = item;
  return (
    <div
      className={classNames(className, 'form-group', size ? `form-group-${size}` : null)}
      {...props}
    />
  );
}

const offsetToggleKeys = ['send_birthday_notification', 'send_memorialized_notification'];
const dependentOnDeceasedKeys = ['memorialized_at', 'send_memorialized_notification'];

export default function FormBuilder({ item, ...props }) {
  const formCtx = useContext(ContactFormContext);

  if (offsetToggleKeys.includes(item.key)) {
    props = { ...props, offsetToggle: true };
  }

  if (dependentOnDeceasedKeys.includes(item.key) && !formCtx.contactIsDeceased) {
    return null;
  }

  switch (item.type) {
    case 'section':
      return <FormSection item={item} {...props} />;
    case 'text':
      return <Text item={item} {...props} />;
    case 'select':
      return <Picker item={item} {...props} />;
    case 'dropdown':
      return <Dropdown item={item} {...props} />;
    case 'repeater':
      return <Repeater item={item} {...props} />;
    case 'toggle':
      return <Toggle item={item} {...props} />;
    case 'year':
      return <Year item={item} {...props} />;
    case 'date':
      return <DateForm item={item} {...props} />;
    case 'birthdate':
      return <DateSelector item={item} {...props} />;
  }

  return null;
}

const indexToValue = (val: string): number | null => {
  return val == '' ? null : parseInt(val);
};

function DatePartSelect({ value, onChange, children, notSetLabel }) {
  return (
    <div className="form-group form-group-3 date-part-select">
      <span className="form-select-container">
        <select className="form-select" value={value} onChange={onChange}>
          <option value="">{notSetLabel}</option>
          {children}
        </select>
      </span>
    </div>
  );
}

function DateSelector({ item, ...props }) {
  const data = props.data || {};
  const current = data[item.key] ?? item.value ?? { month: null, day: null, year: null };

  const onMonthChange = (e) => {
    const month = indexToValue(e.target.value);
    props.onChange(item.key, { ...current, month });
  };

  const onDayChange = (e) => {
    const day = indexToValue(e.target.value);
    props.onChange(item.key, { ...current, day });
  };

  const onYearChange = (e) => {
    const year = indexToValue(e.target.value);
    props.onChange(item.key, { ...current, year });
  };

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      <div className="form">
        <DatePartSelect value={current.month ?? ''} onChange={onMonthChange} notSetLabel="MM">
          {Object.entries(months).map(([key]) => (
            <option value={key} key={key}>
              {key.length === 1 ? `0${key}` : key}
            </option>
          ))}
        </DatePartSelect>
        <DatePartSelect value={current.day ?? ''} onChange={onDayChange} notSetLabel="DD">
          {dayRange.map((day) => (
            <option value={day} key={day}>
              {String(day).length === 1 ? `0${day}` : day}
            </option>
          ))}
        </DatePartSelect>
        <DatePartSelect value={current.year ?? ''} onChange={onYearChange} notSetLabel="YYYY">
          {yearRange.map((year) => (
            <option value={year} key={year}>
              {year}
            </option>
          ))}
        </DatePartSelect>
      </div>
    </Group>
  );
}

export function FormSection({ item, ...props }) {
  return (
    <Section>
      <Section.Title underline>{item.label}</Section.Title>
      <div className="form">
        {item.fields.map((field, i) => (
          <FormBuilder key={i} item={field} {...props} />
        ))}
      </div>
    </Section>
  );
}

export function Repeater({ item, ...props }) {
  const user = (props.data || {})[item.key];
  const data = props.cleared ? user || [{}] : user ?? item.data ?? [];

  function handleChange(i, subItem, key, value) {
    if (subItem.type === 'toggle' && value === true) {
      for (const dataItem of data) {
        dataItem[key] = false;
      }
    }

    data[i][key] = value;
    props.onChange(item.key, data);
  }

  function handleRemove(i, e) {
    e.preventDefault();

    data.splice(i, 1);
    props.onChange(item.key, data);
  }

  function handleAdd(e) {
    e.preventDefault();
    data.push({});
    props.onChange(item.key, data);
  }

  return (
    <Section
      className={classNames(
        'form-group',
        ['all_locations', 'all_websites'].includes(item.key) && 'padded-repeater-group',
      )}
    >
      <Section.Title className="form-repeater-title" underline>
        {item.title}
      </Section.Title>
      <div className="form-repeater">
        {data.map((dataItem, dataIndex) => (
          <div className="form-repeater-item form" key={dataIndex}>
            {item.form.map((field, i) => (
              <FormBuilder
                key={i}
                item={{
                  ...field,
                  value: dataItem[field.key],
                }}
                {...props}
                data={dataItem}
                onChange={handleChange.bind(null, dataIndex, field)}
              />
            ))}
            <div className="form-repeater-actions">
              <button
                className="btn-none form-repeater-action form-repeater-remove"
                onClick={handleRemove.bind(null, dataIndex)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <div className="form-repeater-actions form-repeater-footer">
          <button className="btn-none form-repeater-action" onClick={handleAdd}>
            Add {determineType(item.key)}
          </button>
        </div>
      </div>
    </Section>
  );
}

export function Text({ item, ...props }) {
  const key = item.key;
  const data = props.data || {};

  const inputProps = {
    className: classNames(
      'form-input',
      data[key] && data[key] !== item.value ? 'form-input-active' : null,
    ),
    value: props.cleared ? data[key] || '' : data[key] ?? item.value ?? '',
    name: key,
    onChange: handleInputChange.bind(null, item, props.onChange),
  };

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      {key === 'bio' ? <Textarea {...inputProps} /> : <input {...inputProps} type="text" />}
    </Group>
  );
}

export function Picker({ item, ...props }) {
  const key = item.key;
  const data = props.data || {};
  const active = props.cleared ? data[key] || null : data[key] || item.value;
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(!item.order?.includes(active) ? active || '' : '');
  }, [active]);

  function handleSelect(key) {
    props.onChange(item.key, key);
  }

  function handleInput(e) {
    setValue(e.target.value);
    props.onChange(item.key, e.target.value);
  }

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      <div className="form-button-group-container">
        <div className="form-button-group">
          {Object.entries(item.options).map(([key, value]) => (
            <button
              className={classNames(
                'form-button-group-item',
                active === key ? 'form-button-group-item-active' : null,
              )}
              onClick={handleSelect.bind(null, key)}
              type="button"
              key={key}
            >
              {value}
            </button>
          ))}
        </div>
        {item.custom && (active === item.custom || value.length > 0) && (
          <input
            onChange={handleInput}
            value={value}
            placeholder={item.placeholder}
            className="form-input form-button-group-input"
            type="text"
          />
        )}
      </div>
    </Group>
  );
}

export function Dropdown({ item, ...props }) {
  const isCustomized = item.value && !item.options.includes(item.value);
  const [settingCustomValue, setSettingCustomValue] = useState(false);
  const [customValue, setCustomValue] = useState(isCustomized ? item.value : '');
  const [value, setValue] = useState(customValue || item.value || item.clear);

  const updateValue = (val) => {
    setValue(val);
    props.onChange(item.key, val);
  };

  const options = useMemo(
    () => item.options.concat([customValue, item.custom, item.clear].filter(Boolean)),
    [item.value, customValue],
  );

  function handleInput(e) {
    if (e.target.value === item.custom) {
      setSettingCustomValue(true);
    }

    if (e.target.value === item.clear) {
      updateValue(null);
    } else {
      updateValue(e.target.value);
    }
  }

  useEffect(() => {
    if (settingCustomValue) {
      updateValue(customValue);
    }
  }, [settingCustomValue, customValue]);

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      {!settingCustomValue && (
        <span className="form-select-container">
          <select className="form-select" value={value} onChange={handleInput}>
            {options.map((opt) => (
              <option value={opt} key={opt}>
                {opt}
              </option>
            ))}
          </select>
        </span>
      )}
      {settingCustomValue && (
        <input
          className="form-input"
          style={{ height: 30, paddingTop: 2, paddingBottom: 0, lineHeight: 30 }}
          type="text"
          placeholder={item.custom}
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          onBlur={() => setSettingCustomValue(false)}
          onKeyDown={(e) => e.key === 'Enter' && setSettingCustomValue(false)}
          autoFocus
        />
      )}
    </Group>
  );
}

export function Toggle({ item, ...props }) {
  const data = props.data || {};
  const value = data[item.key] ?? item.value ?? false;

  return (
    <Group
      item={item}
      className={classNames(
        props.offsetToggle && 'offset-toggle-group',
        item.title && 'titled-toggle-group',
      )}
    >
      {item.title && <label className="form-label">{item.title}</label>}
      <div className="form-toggle-container">
        <div className="form-toggle-label">{item.label}</div>
        <div className="form-toggle-action">
          <label className="form-toggle">
            <input
              className="form-toggle-input"
              type="checkbox"
              checked={value === true}
              onChange={handleCheckboxChange.bind(null, item, props.onChange)}
            />
            <span className="form-toggle-switch" />
          </label>
        </div>
      </div>
    </Group>
  );
}

export function DateForm({ item, ...props }) {
  const data = props.data || {};
  const current = data[item.key] ?? item.value ?? null;

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      <div className="form">
        <Month
          noLabel
          item={item}
          {...props}
          onChange={handleDateChange.bind(null, item, 'month', current, props.onChange)}
        />
        <Year
          noLabel
          item={item}
          {...props}
          onChange={handleDateChange.bind(null, item, 'year', current, props.onChange)}
        />
      </div>
    </Group>
  );
}

export function Month({ item, ...props }) {
  const data = props.data || {};
  const value = data[item.key]?.month ?? item.value?.month ?? '';

  return (
    <Group item={item}>
      {props.noLabel ? null : <label className="form-label">{item.label}</label>}
      <span className="form-select-container">
        <select
          className="form-select"
          value={value}
          name={item.key}
          onChange={handleMonthChange.bind(null, item, props.onChange)}
        >
          <option value="">MM</option>
          {Object.entries(months).map(([key]) => (
            <option value={key} key={key}>
              {key.length === 1 ? `0${key}` : key}
            </option>
          ))}
        </select>
      </span>
    </Group>
  );
}

export function Year({ item, ...props }) {
  const data = props.data || {};
  const value = data[item.key]?.year ?? item.value?.year ?? '';

  return (
    <Group item={item}>
      {props.noLabel ? null : <label className="form-label">{item.label}</label>}
      <span className="form-select-container">
        <select
          className="form-select"
          value={value}
          name={item.key}
          onChange={handleYearChange.bind(null, item, props.onChange)}
        >
          <option value="">YYYY</option>
          {yearRange.map((year) => (
            <option value={year} key={year}>
              {year}
            </option>
          ))}
        </select>
      </span>
    </Group>
  );
}

export function handleInputChange(item, onChange, e) {
  return onChange(item.key, e.currentTarget.value);
}

export function handleCheckboxChange(item, onChange, e) {
  return onChange(item.key, e.currentTarget.checked);
}

export function handleYearChange(item, onChange, e) {
  let value = e.currentTarget.value;

  if (!value) {
    value = null;
  } else {
    value = Number.parseInt(value);
  }

  return onChange(item.key, !value ? null : { month: null, year: value });
}

export function handleMonthChange(item, onChange, e) {
  let value = e.currentTarget.value;

  if (!value) {
    value = null;
  } else {
    value = Number.parseInt(value);
  }

  return onChange(item.key, !value ? null : { month: value, year: null });
}

export function handleDateChange(item, type, data, onChange, _key, value) {
  let date = {
    month: data?.month ?? null,
    year: data?.year ?? null,
  };

  value = value || {};
  date[type] = value[type] ?? null;

  if (!date.month && !date.year) {
    date = null;
  }

  return onChange(item.key, date);
}
